import "./LabReportDetailsEditComponent.scss";
import React, {useCallback, useEffect} from "react";
import {CommonService} from "../../../../../shared/services";
import commonService from "../../../../../shared/services/common.service";
import {Grid} from "@material-ui/core";
import ChipComponent from "../../../../../shared/components/chip/ChipComponent";
import {AttachFile} from "@mui/icons-material";
import ButtonComponent from "../../../../../shared/components/core/button/ButtonComponent";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import ColorPickerComponent from "../../../../../shared/components/color-picker/ColorPickerComponent";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import FilePreviewerComponent from "../../../../../shared/components/file-previewer/filePreviewerComponent";
import DialogComponent from "../../../../../shared/components/dialog/dialogComponent";
import FeedbackComponent from "../../../../../shared/components/feedback/feedbackComponent";
import useFetch from "../../../../../shared/hooks/useFetch";
import {Skeleton} from "@mui/material";
import APIConfig from "../../../../../constants/APIConfig";

interface LabReportDetailsEditComponentProps {
    labDetails: any;
    onEditCancel: Function;
    onUpdateComplete: Function;
}

const LabReportDetailsEditComponent = (props: LabReportDetailsEditComponentProps) => {
    const {labDetails, onEditCancel, onUpdateComplete} = props;
    const labLogoRef = React.useRef<any>(null);
    const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
    const [themeColor, setThemeColor] = React.useState({
        r: '241',
        g: '112',
        b: '19',
        a: '1',
    });
    const [labAttachmentPreviewOpen, setLabAttachmentPreviewOpen] = React.useState(false);
    const [logo, setLogo] = React.useState<any>(null);
    const [isLabLogoCancelVisible, setIsLabLogoCancelVisible] = React.useState(false);
    const [newLabLogo, setNewLabLogo] = React.useState(false);
    const [isEdited, setIsEdited] = React.useState(false);

    const {
        response: InHouseLabDetails,
        loading: isInHouseLabDetailsLoading,
        fetchData: fetchInHouseLabDetails
    } = useFetch<{ id: string, cliaId: string, name: string }>();

    const handleLogoUpload = useCallback((fieldName: any, event: any) => {
        const file = event?.target?.files[0];
        const maxAllowedSize = 5 * 1024 * 1024;
        if (event?.target?.files[0].size > maxAllowedSize) {
            CommonService._alert.showToast('File size should be under 5MB.', "error");
            event.target.value = null;
            event.target.files = null;
            return;
        }
        event.target.value = null;
        event.target.files = null;
        setNewLabLogo(true);
        setLogo(file);
        setIsEdited(true);
    }, []);

    const handleAttachmentPreviewOpen = useCallback(() => {
        setLabAttachmentPreviewOpen(true);
        setIsLabLogoCancelVisible(true);
    }, []);

    const handleLabAttachmentDialogClose = useCallback(() => {
        setLabAttachmentPreviewOpen(false);
    }, []);

    const labLogoChangeContinue = useCallback(() => {
        setLabAttachmentPreviewOpen(false);
        setIsLabLogoCancelVisible(false);
        setNewLabLogo(true);
    }, []);

    const generateHTML = useCallback(() => {
        // Dynamic logo and color values
        if (!logo.url) {
            const dynamicLogo = logo;
            const dynamicColor = themeColor;
            const reader = new FileReader();
            reader.onload = () => {
                const dynamicLogoUrl = reader.result;
                const newWindow = window.open();
                newWindow?.document.write(commonService._labReportGeneratorService.generateLabReportHtml(dynamicColor, dynamicLogoUrl));
                newWindow?.document.close();
            };
            reader.readAsDataURL(dynamicLogo);
        } else {
            const dynamicLogoUrl = logo.url;
            const newWindow = window.open();
            newWindow?.document.write(commonService._labReportGeneratorService.generateLabReportHtml(themeColor, dynamicLogoUrl));
            newWindow?.document.close();
        }
    }, [logo, themeColor]);

    const handleReportSetupSubmit = useCallback(async () => {
        setIsSubmitting(true);
        let logoFile = logo;
        if (logoFile?.url) {
            logoFile = await commonService.generateBlobFileFromUrl(logoFile?.url, logoFile?.title, logoFile?.mimeType);
        }
        const formData = new FormData();
        formData.append('image', logoFile);
        formData.append('themeColor', commonService.rgbaToHex(themeColor));
        commonService._lab.addLabReportSetup(labDetails.id, formData)
            .then((res: any) => {
                onUpdateComplete(res.data.logo, res.data.themeColor);
                setIsSubmitting(false);
            })
            .catch((err: any) => {
                console.log(err);
                commonService._alert.showToast(err?.error, "error");
                setIsSubmitting(false);
            })
    }, [labDetails, onUpdateComplete, themeColor, logo]);

    const getInHouseLabDetails = useCallback(() => {
        fetchInHouseLabDetails({
            url: APIConfig.GET_IN_HOUSE_LAB_DETAILS.URL,
            method: APIConfig.GET_IN_HOUSE_LAB_DETAILS.METHOD,
        });
    }, [fetchInHouseLabDetails]);

    useEffect(() => {
        if (labDetails) {
            setThemeColor(commonService.hexToRgba(labDetails?.themeColor));
            setLogo(labDetails?.logo)
            setNewLabLogo(false)
        }
    }, [labDetails]);

    useEffect(() => {
        getInHouseLabDetails();
    }, [getInHouseLabDetails]);

    return (
        <div className={'lab-report-details-edit-component details-tab-data-content'}>
            <div className="attachments-with-title-wrapper">
                <form>
                    <div className="t-form-controls-wrapper">
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={12} md={6} xl={6}>
                                <div className='form-sub-title'>Logo*</div>
                                {
                                    logo ? <>
                                        <ChipComponent
                                            label={'Lab_logo'}
                                            onClick={() => {
                                                handleAttachmentPreviewOpen();
                                            }}
                                            onDelete={() => {
                                                setLogo(null);
                                            }}
                                            icon={<AttachFile/>}
                                        />
                                    </> : <>
                                        <input type={"file"}
                                               className={"display-none"}
                                               ref={labLogoRef}
                                               accept={"image/jpg, image/jpeg, image/png"}
                                               onChange={(event) => {
                                                   handleLogoUpload("", event);
                                               }}
                                        />
                                        <ButtonComponent
                                            variant={"outlined"}
                                            fullWidth={false}
                                            color={"secondary"}
                                            handleClick={() => {
                                                labLogoRef.current?.click()
                                            }}
                                            startIcon={<UploadOutlinedIcon/>}
                                            type={'button'}>
                                            Upload
                                        </ButtonComponent>
                                    </>
                                }
                            </Grid>
                            <Grid item xs={12} sm={12} md={6} xl={6}>
                                <div className='form-sub-title'>Theme Color*</div>
                                <ColorPickerComponent onColorChange={(color: any) => {
                                    setIsEdited(true);
                                    setThemeColor(color);
                                }}
                                                      canEdit={true}
                                                      themeColor={themeColor}
                                />
                            </Grid>
                        </Grid>
                        <div className={"horizontal-line"}/>
                        <ButtonComponent
                            variant={"outlined"}
                            fullWidth={false}
                            color={"secondary"}
                            disabled={!logo || !themeColor}
                            handleClick={() => {
                                generateHTML()
                            }}
                            type={'button'}
                            startIcon={<RemoveRedEyeIcon/>}
                        >
                            Preview Sample Report
                        </ButtonComponent>
                        {
                            InHouseLabDetails?.cliaId !== labDetails?.cliaId && (
                                isInHouseLabDetailsLoading ?
                                    <>
                                        <Skeleton className="mrg-top-15 mrg-bottom-15" variant="text" width={'100%'}
                                                  height={80}/>
                                    </> :
                                    <FeedbackComponent className={'mrg-top-15 mrg-bottom-15'}>
                                        This sample was processed at: {InHouseLabDetails?.name}
                                    </FeedbackComponent>
                            )
                        }
                    </div>
                    <div className='form-action-section-edit'>
                        <ButtonComponent
                            className={"width-25"}
                            type="button"
                            color={"secondary"}
                            variant={"outlined"}
                            fullWidth={false}
                            handleClick={() => onEditCancel()}
                        >
                            Cancel
                        </ButtonComponent>
                        <ButtonComponent
                            className={"width-25"}
                            type="button"
                            color={"primary"}
                            variant={"contained"}
                            isLoading={isSubmitting}
                            fullWidth={false}
                            disabled={!logo || !themeColor || !isEdited}
                            handleClick={() => handleReportSetupSubmit()}
                        >
                            Save
                        </ButtonComponent>
                    </div>
                </form>
            </div>
            <DialogComponent open={labAttachmentPreviewOpen}
                             maxWidth={'md'}
                             onClose={() => {
                                 setLabAttachmentPreviewOpen(false)
                             }}
                             isShowCancel={true}
                             title={'Lab Logo Preview'}
                             className={"attachment-preview-dialog"}>

                <div className="attachment-preview-dialog-body" id="file_preview_component">
                    <FilePreviewerComponent
                        attachmentFile={logo}
                        attachmentUrl={logo?.url}
                        isFile={newLabLogo}/>
                </div>
                {logo &&
                    <div className="uploaded-attachment-file-name">
                        {!isLabLogoCancelVisible ? <>
                                <span className="font-weight-bold"> Uploaded File Name:&nbsp;</span>
                                <span>{logo?.name}</span>
                            </> :
                            <></>
                        }
                    </div>
                }
                <div className="attachment-preview-dialog-footer">
                    {
                        isLabLogoCancelVisible ?
                            <ButtonComponent variant={"outlined"}
                                             id="cancel_btn"
                                             fullWidth={true}
                                             handleClick={() => {
                                                 handleLabAttachmentDialogClose();
                                             }}
                            >
                                Cancel
                            </ButtonComponent> :
                            <>
                                <ButtonComponent variant={"outlined"}
                                                 id="re_upload_btn"
                                                 fullWidth={true}
                                                 handleClick={() => {
                                                     labLogoRef.current?.click()
                                                 }}
                                >
                                    Re-upload
                                </ButtonComponent>&nbsp;

                                <ButtonComponent variant={"contained"}
                                                 id="continue_btn"
                                                 fullWidth={true}
                                                 handleClick={labLogoChangeContinue}>
                                    Confirm Upload
                                </ButtonComponent>
                            </>}
                </div>
            </DialogComponent>
        </div>
    );

};

export default LabReportDetailsEditComponent;
